/**
 * 실행 이력 위 「최초 실패」 한 줄 — 이 실행이 **어디서 처음** 실패했고 무슨 오류였는지.
 *
 * 노드가 많으면 실패 하나 뒤로 「선행 실패」「건너뜀」이 줄줄이 붙어 시작점이 묻힌다. 실행 노드가 부른 자식
 * 워크플로에서 난 오류는 서버가 "자식 › 노드: 오류" 로 끌어와 여기 그대로 보인다(A › B › C 로 이어진다).
 * 서버: main._first_failure / services/run_failure.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FirstFailure } from '../../services/executionApi';

interface Props {
  failure: FirstFailure;
  /** 노드 이름 (모르면 task_id) */
  label: string;
  /** 주면 「로그 보기」 단추가 생긴다 */
  onOpenLog?: () => void;
}

const FirstFailureBanner: React.FC<Props> = ({ failure, label, onOpenLog }) => {
  const { t } = useTranslation();
  const [more, setMore] = useState(false);
  const msg = (failure.message || '').trim();
  const [head, ...rest] = msg.split('\n');
  const fromChild = head.includes(' › ');
  return (
    <div className="mx-2 my-1.5 px-2.5 py-1.5 rounded border border-red-200 bg-red-50 text-xs">
      <div className="flex items-start gap-2">
        <span className="shrink-0 font-semibold text-red-700">{t('execution:failure.first')}</span>
        <span className="min-w-0 flex-1">
          <span className="font-medium text-gray-800">{label}</span>
          {failure.try_number && failure.try_number > 1 ? <span className="ml-1 text-gray-500">{t('execution:failure.try', { n: failure.try_number })}</span> : null}
          {head
            ? <span className="block text-red-700 break-words" title={msg}>{head}</span>
            : <span className="block text-gray-500">{t('execution:failure.noMessage')}</span>}
          {fromChild && <span className="block text-[0.6875rem] text-gray-500">{t('execution:failure.fromChild')}</span>}
        </span>
        <span className="shrink-0 flex gap-2">
          {rest.join('').trim() && (
            <button type="button" className="text-blue-700 hover:underline" onClick={() => setMore((v) => !v)}>
              {more ? t('execution:failure.less') : t('execution:failure.more')}
            </button>
          )}
          {onOpenLog && <button type="button" className="text-blue-700 hover:underline" onClick={onOpenLog}>{t('execution:failure.openLog')}</button>}
        </span>
      </div>
      {more && <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[0.6875rem] text-gray-700">{msg}</pre>}
    </div>
  );
};

/** 실행은 「실패」인데 실패한 노드가 없을 때 — 중지했거나 노드가 돌기 전에 끝났다 */
export const NoNodeFailureNote: React.FC = () => {
  const { t } = useTranslation();
  return <div className="mx-2 my-1.5 px-2.5 py-1.5 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800">{t('execution:failure.noNode')}</div>;
};

export default FirstFailureBanner;
